import React, { useState } from 'react';
import { youtubeId, youtubeThumbnail } from '../utils/helpVideos';

// Édition des vidéos d'aide d'un guide (Gestion infos) : icône, mots-clés,
// liste de vidéos YouTube. Enregistre via l'action « updateHelp » de l'API guides.
const GuideHelpEditor = ({ guide, baseUrl, onSaved, onError }) => {
  const [icon, setIcon] = useState(guide.icon || '');
  const [keywords, setKeywords] = useState(guide.keywords || '');
  const [videos, setVideos] = useState(() =>
    (Array.isArray(guide.videos) ? guide.videos : []).map((video) => ({ ...video }))
  );
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const touch = (setter) => (value) => {
    setter(value);
    setIsDirty(true);
  };

  const updateVideo = (index, field, value) => {
    setVideos((previous) => previous.map((video, i) => (i === index ? { ...video, [field]: value } : video)));
    setIsDirty(true);
  };

  const moveVideo = (index, direction) => {
    setVideos((previous) => {
      const target = index + direction;
      if (target < 0 || target >= previous.length) return previous;
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setIsDirty(true);
  };

  const removeVideo = (index) => {
    setVideos((previous) => previous.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleSave = async () => {
    const cleaned = videos
      .map((video) => ({ title: (video.title || '').trim(), youtube: (video.youtube || '').trim() }))
      .filter((video) => video.title || video.youtube);

    const invalid = cleaned.find((video) => !youtubeId(video.youtube));
    if (invalid) {
      onError(`Lien YouTube invalide pour « ${invalid.title || 'sans titre'} ».`);
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${baseUrl}/api/guides/config.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'updateHelp',
          guideId: guide.id,
          icon: icon.trim(),
          keywords: keywords.trim(),
          videos: cleaned
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Erreur lors de l’enregistrement des vidéos.');
      }
      setVideos(cleaned);
      setIsDirty(false);
      onSaved(payload.guide || { ...guide, icon: icon.trim(), keywords: keywords.trim(), videos: cleaned });
    } catch (err) {
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="guide-help-editor">
      <h4>🎬 Vidéos d’aide &amp; mots-clés</h4>

      <div className="guide-help-row">
        <label className="guide-help-icon">
          Icône
          <input type="text" value={icon} onChange={(e) => touch(setIcon)(e.target.value)} placeholder="💧" maxLength={8} />
        </label>
        <label className="guide-help-keywords">
          Mots-clés (toutes langues, séparés par des virgules)
          <textarea
            rows={2}
            value={keywords}
            onChange={(e) => touch(setKeywords)(e.target.value)}
            placeholder="eau, pas d'eau, pression, douche froide, acqua, water, Wasser…"
          />
        </label>
      </div>

      {videos.length > 0 && (
        <ul className="guide-help-videos">
          {videos.map((video, index) => {
            const id = youtubeId(video.youtube);
            return (
              <li key={index} className="guide-help-video">
                <div className="guide-help-thumb">
                  {id ? <img src={youtubeThumbnail(id)} alt="" /> : <span>{video.youtube ? '⚠️' : '—'}</span>}
                </div>
                <div className="guide-help-fields">
                  <input
                    type="text"
                    value={video.title || ''}
                    onChange={(e) => updateVideo(index, 'title', e.target.value)}
                    placeholder="Titre (ex. Couper l'arrivée d'eau)"
                  />
                  <input
                    type="url"
                    value={video.youtube || ''}
                    onChange={(e) => updateVideo(index, 'youtube', e.target.value)}
                    placeholder="https://youtu.be/…"
                  />
                </div>
                <div className="guide-help-video-actions">
                  <button type="button" onClick={() => moveVideo(index, -1)} disabled={index === 0} title="Monter">↑</button>
                  <button type="button" onClick={() => moveVideo(index, 1)} disabled={index === videos.length - 1} title="Descendre">↓</button>
                  <button type="button" onClick={() => removeVideo(index)} title="Retirer">✕</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="guide-help-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setVideos((previous) => [...previous, { title: '', youtube: '' }]);
            setIsDirty(true);
          }}
        >
          + Ajouter une vidéo
        </button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || !isDirty}>
          {saving ? '⏳ Enregistrement…' : isDirty ? 'Enregistrer' : 'Enregistré ✓'}
        </button>
      </div>
    </div>
  );
};

export default GuideHelpEditor;
